import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useDeleteTechnician } from '@/hooks/useTechnicians'
import { getApiErrorMessage } from '@/services/api'
import type { Technician } from '@/types'

interface Props {
  technician: Technician | null
  // 2.10.8-015 — block delete when future assigned jobs exist.
  hasFutureJobs?: boolean
  onClose: () => void
  onDeleted?: () => void
  redirectToList?: boolean
  // Offer to deactivate instead when blocked.
  onDeactivateInstead?: () => void
}

// 2.10.8 — Delete technician (GDPR soft delete + anonymisation) with guard.
// Figma 1336:31123.
export function DeleteTechnicianDialog({
  technician,
  hasFutureJobs,
  onClose,
  onDeleted,
  redirectToList,
  onDeactivateInstead,
}: Props) {
  const { t } = useTranslation('technicians')
  const navigate = useNavigate()
  const del = useDeleteTechnician()

  const confirm = async () => {
    if (!technician) return
    try {
      await del.mutateAsync(technician.id)
      toast({ variant: 'success', title: t('deleteDialog.deleted') })
      onDeleted?.()
      if (redirectToList) navigate('/technicians')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('deleteDialog.deleteError')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!technician}
      onOpenChange={o => !o && onClose()}
      title={t('deleteDialog.title')}
      description={
        hasFutureJobs
          ? t('deleteDialog.futureJobsBlock')
          : t('deleteDialog.message')
      }
      confirmLabel={t('deleteDialog.confirm')}
      onConfirm={confirm}
      busy={del.isPending}
      alternateAction={
        hasFutureJobs && onDeactivateInstead
          ? {
              label: t('deleteDialog.deactivate'),
              onClick: onDeactivateInstead,
            }
          : undefined
      }
    />
  )
}

export default DeleteTechnicianDialog
